<template>
  <section class="records" :class="{ 'crm-records': isBuildCRM() }">
    <slot name="filterPanel"></slot>
    <div v-if="!apiStatus.recordData.fail && !apiStatus.recordData.loading" class="records-list">
      <slot name="tableTop"></slot>
      <div class="outer-table-wrapper">
        <div :class="{ 'table-min-height': haveMinHeight, 'table-wrapper': true }">
          <table class="table-desktop striped">
            <template v-for="(colSetting, index) in recordsData.colSetting" :key="index">
              <col v-if="recordsLength" :style="colSetting" />
            </template>
            <thead>
              <tr v-if="recordsLength">
                <th
                  v-for="(value, key) in headingObj"
                  :key="key"
                  :class="{
                    'right-aligned': isRightAligned(headingObj, key),
                    'first-col': key === Object.keys(headingObj)[0],
                  }"
                >
                  {{ value }}
                </th>
              </tr>
            </thead>
            <tbody v-if="recordsLength">
              <tr v-for="(record, i) in recordsData.data" :key="`desktop view row ${i + 1}`">
                <td v-for="(value, key) in headingObj" :key="`desktop view heading ${key}`" :class="{ 'first-col': key === Object.keys(headingObj)[0] }" :id="key">
                  <template v-if="isDropDown(key)">
                    <!-- Card status column will be displayed as a dropdown list -->
                    <DropdownSelect
                      :name="`${recordsData.data[i][colName]}-${colHeader}`"
                      :items="activedDropDownList"
                      v-model="recordsData.data[i][colHeader]"
                      :initialSelect="record[key]"
                      :disabled="record[key] === dropDownList.disabledItem"
                      noDisabled
                    />
                  </template>
                  <template v-else-if="isLink(key)">
                    <button
                      class="non-button-style"
                      v-if="isCRM && !crmNavigateParams"
                      :aria-label="`Link to quote ${record[key].toUpperCase()}`"
                      @click="
                        crmNavigatePage(CRM_PAGES.RESULT, {
                          quoteId: record.id,
                          accessingPersonCode: record.personCode,
                          productCode: record.productCode,
                        })
                      "
                    >
                      {{ record[key] }}
                    </button>
                    <button
                      class="non-button-style"
                      v-if="isCRM && crmNavigateParams"
                      :aria-label="`Link to ${record[key].toUpperCase()}`"
                      @click="
                        crmNavigatePage(crmNavigateParams.page, {
                          ...record,
                          ...crmNavigateParams.routeParams,
                        })
                      "
                    >
                      {{ record[key] }}
                    </button>
                    <router-link
                      v-else-if="!isCRM && !isExternalLink"
                      :aria-label="`Link to quote ${record[key].toUpperCase()}`"
                      :to="{
                        name: recordLink.recordLinkParams.toName,
                        params: {
                          quoteId: record.id,
                          accessingPersonCode: record.personCodes ? record.personCodes[0] : record.personCode,
                        },
                        state: {
                          claimId: record.id,
                          productCode: record.productCode,
                        },
                      }"
                    >
                      {{ record[key] }}
                    </router-link>
                    <!--if recordLinkParams is undefined, recordLinkFunction return index by default-->
                    <a
                      v-else-if="!isCRM && isExternalLink"
                      :aria-label="`Link to ${$route.meta.title} for ${record[key]}`"
                      :title="`${$route.meta.title} file`"
                      v-on:click="recordLink.recordLinkFunction(recordLink.recordLinkParams ? record[recordLink.recordLinkParams] : i)"
                    >
                      {{ record[key] }}
                    </a>
                  </template>
                  <template v-else-if="isDate(key)">
                    <!-- Transform Date format from 'YYYY-MM-DD' to 'DD/MM/YYYY' -->
                    {{ reverseFormatFormDate(record[key]).replace(/\s+/g, '') }}
                  </template>
                  <div v-else class="list-item" :class="[record.isBold ? 'bolded-record' : '', isRightAligned(headingObj, key) ? 'right-aligned' : '']">
                    {{ record[key] ? record[key] : '' }}
                    <span v-if="record[key] == null || record[key] == ''" v-html="defaultNoData" />
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-if="!isFilterApplied">
                <td colspan="4" class="no-records-desktop">
                  <span v-html="recordsData.noRecordsText"></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <slot name="tableBottom"></slot>
      <slot name="tablePagination"></slot>
    </div>
  </section>
</template>

<script>
import isEqual from 'lodash.isequal';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import { reverseFormatFormDate, removeWhitespaceFromString } from '@general/js/utils';
import { isBuildCRM } from '@/utils';
import { TableColAlignment } from '@/enums';

const CRM_PAGES = {
  LANDING: 'BenefitQuotesAndClaims',
  GABQ: 'GABQnMANC',
  RESULT: 'QuoteResultClaimSubmission',
};

export default {
  name: 'RecordsTable',

  components: {
    DropdownSelect,
  },

  props: {
    /**
     * Holds api status for data gathered from api calls
     * Contains properties that contain 'fail and 'loading' properties:
     * - refData, optional
     * - recordData
     * - recordDocument, optional
     */
    apiStatus: {
      type: Object,
      required: true,
    },

    /**
     * Holds information on all records for table
     * Contains the following properties:
     * - headings: List of headings for table
     * - data: List of objects containing data
     * - noRecordsText: text for when there are no records available
     * - colSetting: The column settings for things like width. Currently only supports width.
     * - colAlignments: An array holding the text alignments for each col in the table
     * e.g. headings = ['Number', ''Member', 'Date']
     *      data = [{Number: 2, Member: 'AA', Date: 01/02/2003}, {Number: 1 ...}]
     *      noRecordsText = 'No records'
     *      colSetting = [{width: '20px'}]
     *      colAlignments = ['left', 'left', 'left', 'right', 'right', 'right']
     */
    recordsData: {
      required: true,
      headings: {
        required: true,
        type: [Array, Object],
      },
      data: {
        required: true,
        type: Array,
      },
      noRecordsText: {
        required: false,
        type: String,
      },
      colSetting: {
        required: false,
        type: [Array, Object],
        default: [],
      },
      colAlignments: {
        required: false,
        type: Array,
      },
    },

    /**
     * optional, used to check the change of recordsData.data
     */

    initialData: {
      type: Array,
    },

    /**
     * If the Record data column need to display as a dropdown list
     * Includes: name:String, columnHeader:String
     * Dropdown item name will set as `${name}-${columnHeader}`
     */
    dropDownColumn: {
      type: Object,
    },

    dropDownList: {
      type: Object,
      // Dropdown list item which will be set as disabled, optional
      disabledItem: {
        type: String,
      },
      // Each item in this array has name and value properties
      dropDownItems: {
        type: Array,
      },
    },

    /**
     * If the Record data (first column) is an external link
     * If external link, it will run the recordLinkFunction (prop)
     * If not external link, it will run router-view to send to internal page
     */
    linkColumn: {
      index: {
        type: Number,
      },
      isExternalLink: {
        type: Boolean,
      },
      isCRM: {
        type: Boolean,
      },
    },

    recordLink: {
      recordLinkFunction: {
        type: Function,
      },

      // Use to decide which parameters need to be returned within recordLinkFunction
      recordLinkParams: {
        type: String,
      },
    },

    /**
     * Function that referred from CRMSupportMixins
     */
    crmNavigatePage: {
      type: Function,
    },

    /**
     * CRM Navigate function parameters
     */
    crmNavigateParams: {
      type: Object,
    },

    /**
     *  Whether the page includes a filter or not
     */
    isFilterApplied: {
      type: Boolean,
      default: false,
    },

    /**
     * Spaces should default to either -- or blank space
     */
    defaultNoData: {
      type: String,
      default: '&#8212;',
    },

    /**
     * This is to provide tables with minimum height as required.
     */
    haveMinHeight: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      CRM_PAGES,
    };
  },

  watch: {
    'recordsData.data': {
      handler(recordsList) {
        // Only when card status changed compared with initial status
        let changedRecordList = [];
        if (this.isDataChanged()) {
          changedRecordList = recordsList.filter((record) => {
            return this.initialData.find((initialRecord) => {
              return (initialRecord.cardId === record.cardId && initialRecord.status !== record.status) ||
                (initialRecord.Services === record.Services && initialRecord['Apply waiver'] !== record['Apply waiver']);
            });
          });
        }
        this.$emit('recordDataChanged', {
          isDataChanged: this.isDataChanged(),
          changedRecordList,
        });
      },
      deep: true,
    },
  },

  computed: {
    recordsLength() {
      return this.recordsData.data.length;
    },

    colName() {
      if (this.dropDownColumn !== undefined) {
        return this.dropDownColumn.name;
      }
      return null;
    },

    colHeader() {
      if (this.dropDownColumn !== undefined) {
        return this.dropDownColumn.columnHeader;
      }
      return null;
    },

    linkName() {
      if (this.linkColumn !== undefined && Number.isInteger(this.linkColumn.index)) {
        return this.recordsData.headings[this.linkColumn.index];
      }
      return null;
    },

    isExternalLink() {
      return !!this.linkColumn.isExternalLink;
    },

    isCRM() {
      return !!this.linkColumn.isCRM;
    },

    // If recordsData.headings is Array, convert it into Object
    headingObj() {
      if (Array.isArray(this.recordsData.headings)) {
        const newHeadingObj = {};
        this.recordsData.headings.forEach((el) => {
          newHeadingObj[el] = el;
        });
        return newHeadingObj;
      }
      return this.recordsData.headings;
    },

    // Only return actived dropdown items
    activedDropDownList() {
      return this.dropDownList.dropDownItems
        .filter((item) => {
          return item.value !== this.dropDownList.disabledItem;
        })
        .map((item) => {
          return { ...item, name: item.value };
        });
    },
  },

  methods: {
    // Check if any cards status change
    isDataChanged() {
      if (this.recordsData.data !== undefined && this.initialData !== undefined) {
        let isChanged = !isEqual(this.initialData, this.recordsData.data);
        return isChanged;
      }
      return false;
    },

    // Check if there is a column data is a dropdown list
    isDropDown(key) {
      if (key === this.colHeader) {
        return true;
      }
      return false;
    },

    // Check if there is a column data is a link
    isLink(key) {
      if (key === this.linkName) {
        return true;
      }
      return false;
    },

    // If the column value is Date, return true else return false
    isDate(key) {
      const dateHeaderArray = ['issueDate', 'expiryDate'];
      if (dateHeaderArray.includes(key)) {
        return true;
      }
      return false;
    },

    // This function determines whether the text in the column should be right aligned based on colAlignments
    isRightAligned(headingObj, key) {
      const colNames = Object.keys(headingObj);

      if (this.recordsData.colAlignments && Array.isArray(this.recordsData.colAlignments) && this.recordsData.colAlignments.length === colNames.length) {
        const colNum = colNames.indexOf(key);
        return this.recordsData.colAlignments[colNum] === TableColAlignment.RIGHT;
      }

      return false;
    },

    reverseFormatFormDate,
    removeWhitespaceFromString,
    isBuildCRM,
  },
};
</script>

<style lang="scss" scoped>
table {
  margin: spacer(3) 0;
  background-image: none;
  display: table;
  width: 100%;
  border: none;

  .dropdown-select-wrapper {
    z-index: 0;
  }
}

.records-list {
  .table-min-height {
    min-height: 450px;
  }

  @include mobile {
    .outer-table-wrapper::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: calc(100% - #{$MOBILE_PADDING});
      width: 40px;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.75) 100%);
    }
  }

  .table-wrapper {
    border: 1px solid $GREY_300;

    @include tablet-and-above {
      border-bottom: none;
    }

    @include mobile {
      min-height: unset;
      overflow-x: auto;
    }

    .message {
      margin: 1.25rem 0;
      text-align: center;
    }

    .no-records-desktop {
      font-weight: $NORMAL;
      border-top: 2px solid $LIGHT_GREEN;
      text-align: center;
      padding: 25px;
    }

    .no-records-mobile {
      font-weight: $NORMAL;
      text-align: center;
      padding: 25px;
    }

    .btn-sticky {
      position: sticky;
      bottom: rem(30);
      margin-bottom: rem(30);
    }

    @include tablet-and-above {
      button {
        float: right;
        z-index: 3;

        &.non-button-style {
          float: left;
        }
      }

      .btn-sticky {
        margin-right: rem(10);
      }
    }
  }

  table {
    background-image: none;
    table-layout: fixed;
    display: table;

    &.table-desktop {
      width: 100%;
      margin: 0;

      .list-item {
        overflow-wrap: break-word;
      }
    }

    .bolded-record {
      font-weight: $BOLD;
    }
  }
}

// style for mobile
@include mobile {
  table {
    border-collapse: separate;
    border-spacing: 0;

    td,
    th {
      border: none;
    }
  }

  .table-wrapper {
    border-bottom: 1px solid $GREY_300;
    margin-right: -20px;
  }

  .first-col {
    left: 0;
    position: sticky;
    z-index: 2;
    box-shadow: 2px 0px 2px 0px rgba($GREY_800, 0.05);
  }
}

// For CRM table style only
.crm-records {
  p {
    font-weight: $NORMAL;
  }

  .records-list {
    .table-wrapper {
      th {
        font-weight: $BOLD;
      }

      td {
        font-weight: $NORMAL;
      }

      .no-records-desktop,
      .no-records-mobile {
        span {
          font-weight: $NORMAL;
        }
      }
    }
  }
}
.right-aligned {
  text-align: right;
}
</style>